// src/components/auth/RegisterForm.tsx

import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Alert,
    Typography,
    Grid,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Link,
} from '@mui/material';
import {
    Lock as LockIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Telegram as TelegramIcon,
    Badge as BadgeIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Public as VkIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { RegisterRequest } from '../../types';
import PrivacyPolicyDialog from '../about/PrivacyPolicyDialog';

export interface RegisterFormRef {
    submit: () => void;
}

interface RegisterFormProps {
    invitationToken: string;
    initialData?: {
        fullName?: string;
        phone?: string;
        telegram?: string;
        vk?: string;
    };
}

const RegisterForm = forwardRef<RegisterFormRef, RegisterFormProps>(({
                                                                         invitationToken,
                                                                         initialData,
                                                                     }, ref) => {
    const navigate = useNavigate();
    const { register } = useAuth();

    const [formData, setFormData] = useState<RegisterRequest>({
        password: '',
        email: '',
        fullName: initialData?.fullName || '',
        phoneNumber: initialData?.phone || '+7',
        telegram: initialData?.telegram || '',
        vk: initialData?.vk || '',
        invitationToken,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState<string>('');
    const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
    const [policyDialogOpen, setPolicyDialogOpen] = useState(false);

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (serverError) {
            setServerError('');
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;

        if (!value.startsWith('+7')) {
            const digits = value.replace(/\D/g, '');
            if (digits.length > 0 && !digits.startsWith('7')) {
                value = '+7' + digits;
            } else if (digits.startsWith('7')) {
                value = '+' + digits;
            } else {
                value = '+7';
            }
        }

        const digitsAfterPrefix = value.slice(2).replace(/\D/g, '').slice(0, 10);
        value = '+7' + digitsAfterPrefix;

        setFormData(prev => ({ ...prev, phoneNumber: value }));
        if (errors.phoneNumber) {
            setErrors(prev => ({ ...prev, phoneNumber: '' }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.password) {
            newErrors.password = 'Введите пароль';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Пароль должен быть минимум 8 символов';
        } else if (formData.password.length > 100) {
            newErrors.password = 'Пароль не должен превышать 100 символов';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Введите email';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Некорректный email';
        }

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Введите ФИО';
        } else if (formData.fullName.length > 100) {
            newErrors.fullName = 'ФИО не должно превышать 100 символов';
        }

        if (!formData.phoneNumber) {
            newErrors.phoneNumber = 'Введите номер телефона';
        } else if (!/^\+7\d{10}$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = 'Формат: +7XXXXXXXXXX (11 цифр)';
        }

        // Telegram — необязательное, но если заполнено — проверяем формат
        const telegram = formData.telegram?.trim() || '';
        if (telegram && !/^@?[a-zA-Z][a-zA-Z0-9_]{3,30}[a-zA-Z0-9]$/.test(telegram)) {
            newErrors.telegram = '5-32 символов, начинается с буквы, только a-z, A-Z, 0-9, _';
        }

        // VK — необязательное
        const vk = formData.vk?.trim() || '';
        if (vk) {
            const vkRegex = /^(?:https?:\/\/)?(?:www\.)?vk\.com\/[a-zA-Z0-9_.]+$|^@?[a-zA-Z0-9_.]+$/;
            if (!vkRegex.test(vk)) {
                newErrors.vk = 'Введите ссылку на VK или username';
            }
        }

        if (!agreeWithPolicy) {
            newErrors.agreeWithPolicy = 'Необходимо согласие с политикой конфиденциальности';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Определить, к какому полю относится ошибка бэкенда
    const parseBackendError = (errorMessage: string): { field: string; message: string } | null => {
        const lowerMessage = errorMessage.toLowerCase();

        // Email
        if (lowerMessage.includes('email')) {
            const match = errorMessage.match(/:\s*(.+)$/);
            const value = match ? match[1] : '';
            return {
                field: 'email',
                message: `Email "${value}" уже зарегистрирован`,
            };
        }

        // Phone
        if (lowerMessage.includes('phone')) {
            const match = errorMessage.match(/:\s*(.+)$/);
            const value = match ? match[1] : '';
            return {
                field: 'phoneNumber',
                message: `Номер телефона "${value}" уже занят`,
            };
        }

        // Telegram
        if (lowerMessage.includes('telegram')) {
            const match = errorMessage.match(/:\s*(.+)$/);
            const value = match ? match[1] : '';
            return {
                field: 'telegram',
                message: `Telegram "${value}" уже занят`,
            };
        }

        // VK
        if (lowerMessage.includes('vk')) {
            const match = errorMessage.match(/:\s*(.+)$/);
            const value = match ? match[1] : '';
            return {
                field: 'vk',
                message: `VK "${value}" уже занят`,
            };
        }

        return null;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setServerError('');

        try {
            await register({
                password: formData.password,
                email: formData.email.trim(),
                fullName: formData.fullName.trim(),
                phoneNumber: formData.phoneNumber,
                telegram: formData.telegram?.trim() || '',
                vk: formData.vk?.trim() || '',
                invitationToken,
            });

            navigate('/dashboard', { replace: true });
        } catch (error: any) {
            const backendError = error?.response?.data?.error;

            if (backendError) {
                // Пытаемся определить поле по тексту ошибки
                const parsed = parseBackendError(backendError);

                if (parsed) {
                    // Устанавливаем ошибку на конкретное поле
                    setErrors(prev => ({
                        ...prev,
                        [parsed.field]: parsed.message
                    }));

                    // Скроллим к полю с ошибкой (опционально)
                    // fieldRefs[parsed.field]?.current?.focus();
                } else {
                    // Не удалось определить поле — показываем общую ошибку
                    setServerError(backendError);
                }
            } else if (error?.response?.status === 409) {
                setServerError('Пользователь с такими данными уже существует');
            } else if (error?.response?.status === 400) {
                setServerError(error?.response?.data?.message || 'Неверные данные');
            } else if (error?.message) {
                setServerError(error.message);
            } else {
                setServerError('Произошла ошибка при регистрации. Попробуйте позже.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {serverError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {serverError}
                </Alert>
            )}

            <Grid container spacing={2}>
                {/* Телефон */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Номер телефона"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handlePhoneChange}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber || 'Будет использоваться для входа'}
                        disabled={loading}
                        autoComplete="tel"
                        autoFocus
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Пароль */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Пароль"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        error={!!errors.password}
                        helperText={errors.password || 'Минимум 8 символов'}
                        disabled={loading}
                        autoComplete="new-password"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* ФИО */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="ФИО"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        error={!!errors.fullName}
                        helperText={errors.fullName}
                        disabled={loading}
                        autoComplete="name"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <BadgeIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Email */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={loading}
                        autoComplete="email"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Telegram */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="Telegram (необязательно)"
                        name="telegram"
                        value={formData.telegram || ''}
                        onChange={handleChange}
                        error={!!errors.telegram}
                        helperText={errors.telegram || 'Например: @username'}
                        disabled={loading}
                        placeholder="@username"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <TelegramIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* VK */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="VK (необязательно)"
                        name="vk"
                        value={formData.vk || ''}
                        onChange={handleChange}
                        error={!!errors.vk}
                        helperText={errors.vk || 'Ссылка на профиль или username'}
                        disabled={loading}
                        placeholder="https://vk.com/username"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <VkIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
            </Grid>

            {/* Чекбокс согласия с политикой */}
            <Box sx={{ mt: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={agreeWithPolicy}
                            onChange={(e) => {
                                setAgreeWithPolicy(e.target.checked);
                                if (errors.agreeWithPolicy) {
                                    setErrors(prev => ({ ...prev, agreeWithPolicy: '' }));
                                }
                            }}
                            disabled={loading}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            Я согласен с{' '}
                            <Link
                                component="button"
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setPolicyDialogOpen(true);
                                }}
                                sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                            >
                                политикой конфиденциальности
                            </Link>
                        </Typography>
                    }
                />
                {errors.agreeWithPolicy && (
                    <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agreeWithPolicy}
                    </FormHelperText>
                )}
            </Box>

            <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading || !agreeWithPolicy}
                sx={{ mt: 3, mb: 2 }}
            >
                {loading ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Уже есть аккаунт?{' '}
                    <Link
                        component="button"
                        type="button"
                        onClick={() => navigate('/login')}
                        sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                    >
                        Войти
                    </Link>
                </Typography>
            </Box>

            {/* Диалог политики конфиденциальности */}
            <PrivacyPolicyDialog
                open={policyDialogOpen}
                onClose={() => setPolicyDialogOpen(false)}
            />
        </Box>
    );
});

RegisterForm.displayName = 'RegisterForm';

export default RegisterForm;